import React, { useState, useEffect } from 'react';
import { localePath, PAYMENT_PROCESSOR, IS_EUROPE, HAS_AD_REWARD, HAS_SUBSCRIPTIONS, CURRENCY_SYMBOL, CURRENCY_BEFORE_AMOUNT } from '../config/region';
import { API_URLS } from '../config/api';
import { apiGet, apiPost } from '../utils/apiUtils';
import { pushEcommerce, reportPayClick } from '../utils/ecommerce';
import { t } from '../i18n';
import ParticleBackground from './ParticleBackground';
import { ChevronLeft, Star, CreditCard, Clock, Crown } from 'lucide-react';
import './PaymentPage.css';

/*
 * How a price is written. One place, so no call site assumes rubles.
 *
 * The currency comes from the backend's packages response rather than from a build-time constant:
 * frontend and backend deploy separately, and a frontend that hardcoded "$" would render a ruble
 * price it received as "$49.00" for the whole window between the two deploys. Falling back to the
 * region default only matters against a backend too old to report a currency at all.
 */
const SYMBOLS = { USD: '$', RUB: '₽', EUR: '€' };
const SYMBOL_FIRST = new Set(['USD', 'EUR']);

// `digits` caps the decimals instead of the usual cents rounding: a per-turn price is a fraction of a
// cent ($0.0278), and cent rounding would show every pack as the same $0.03.
const formatMoney = (amount, currency, digits) => {
  // No currency reported means a backend from before the field existed, and that backend only ever
  // quoted rubles — so RUB is the accurate reading, not this build's own region default. Getting
  // this wrong would print a ruble amount as "$49.00" for the whole window between the two deploys.
  const effective = currency || 'RUB';
  const symbol = SYMBOLS[effective] || CURRENCY_SYMBOL;
  const before = SYMBOL_FIRST.has(effective);
  const n = Number(amount);
  const value = digits !== undefined ? String(parseFloat(n.toFixed(digits)))
    : Number.isInteger(n) ? String(n) : n.toFixed(2);
  return before ? `${symbol}${value}` : `${value} ${symbol}`;
};

// Marketing copy for the plan cards. Numbers still come from /subscription; only the wording and
// the perks that have no data behind them (early access, Telegram chat) live here.
const PLAN_COPY = {
  standard: {
    tagline: 'Для тех, кто пользуется площадкой регулярно',
    turns: 2500,
    savings: 'Экономия 5%',
    perks: [
      { bold: '25 000 звёзд', hint: 'до 2 500 ходов' },
      { bold: 'Скидка 5%', text: 'на любые пакеты звёзд', hint: 'Применяется автоматически' },
      { bold: '+30 звёзд каждый день', hint: 'Забирайте в профиле — эти звёзды остаются навсегда' },
      { bold: 'Звёзды тарифа действуют 30 дней', hint: 'Неиспользованные сгорают в конце периода' },
    ],
  },
  premium: {
    tagline: 'Максимум возможностей + прямое общение с командой',
    turns: 5000,
    savings: 'Экономия 8% + эксклюзивный доступ',
    ribbon: 'Лучшее предложение',
    perks: [
      { bold: '50 000 звёзд', hint: 'до 5 000 ходов · вдвое больше, чем в Стандарте' },
      { bold: 'Скидка 10%', text: 'на любые пакеты звёзд' },
      { bold: '+50 звёзд каждый день', hint: 'Забирайте в профиле — эти звёзды остаются навсегда' },
      { bold: 'Ранний доступ', text: 'к новым моделям ИИ и новеллам', hint: 'До публичного релиза' },
      { bold: 'Закрытый Telegram-чат', text: 'с основателями площадки',
        hint: 'Задайте любой вопрос. Для приглашения напишите @novelladmin' },
      { bold: 'Звёзды тарифа действуют 30 дней', hint: 'Неиспользованные сгорают в конце периода' },
    ],
  },
};

const PLAN_NOTE = 'Разовая покупка. Автосписания не будет. Доступ к преимуществам — 30 дней.';

const PlanBullets = ({ perks }) => (
  <ul className="pp-plan-bullets">
    {perks.map((perk, i) => (
      <li key={i}>
        <span>
          <b>{perk.bold}</b>{perk.text ? ` ${perk.text}` : ''}
          {perk.hint && <span className="pp-plan-hint">{perk.hint}</span>}
        </span>
      </li>
    ))}
  </ul>
);

const ru = (n) => n.toLocaleString('ru-RU');

const PaymentPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentPackages, setPaymentPackages] = useState([]);
  // Reported by the backend; null until the packages load, or against an older backend.
  const [currency, setCurrency] = useState(null);
  const [purchasingIndex, setPurchasingIndex] = useState(null);

  // Bound to whatever currency the backend reported for this request.
  const money = (amount, digits) => formatMoney(amount, currency, digits);
  const [adLoading, setAdLoading] = useState(false);
  const [adMessage, setAdMessage] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null); // e.g. 25 means 25% off
  const [promoError, setPromoError] = useState(null);
  const [promoApplied, setPromoApplied] = useState(''); // the validated code string
  const [promoLoading, setPromoLoading] = useState(false);
  const [starPromoMessage, setStarPromoMessage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [purchasingPlan, setPurchasingPlan] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchPaymentPackages();
    // Europe has no subscription; the endpoint 404s there, so do not ask.
    if (HAS_SUBSCRIPTIONS) fetchSubscription();
    if (user?.username) {
      fetchPaymentHistory();
    }
  }, [user]);

  useEffect(() => {
    if (!paymentPackages.length) return;
    const sorted = paymentPackages.slice().sort((a, b) => a.new_rubles - b.new_rubles);
    const products = sorted.map((pkg, index) => ({
      id: String(pkg.stars),
      name: `${pkg.stars} звёзд`,
      price: pkg.new_rubles,
      category: 'Звёзды',
      list: 'Пакеты',
      position: index + 1,
    }));
    pushEcommerce({ impressions: products });
    pushEcommerce({ detail: { products } });
  }, [paymentPackages]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await apiGet(API_URLS.USER_ME, {}, false, '', true);
      setUser(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await apiGet(API_URLS.PAYMENT_HISTORY, {}, false, '', true);
      setPaymentHistory(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    }
  };

  const fetchPaymentPackages = async () => {
    try {
      const response = await apiGet(API_URLS.PAYMENT_PACKAGES, {}, false, '', true);
      setPaymentPackages(response.data.packages || []);
      setCurrency(response.data.currency || null);
    } catch (error) {
      console.error('Failed to fetch payment packages:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await apiGet(API_URLS.SUBSCRIPTION, {}, false, '', true);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoLoading(true);
    setPromoError(null);
    setPromoDiscount(null);
    setPromoApplied('');
    setStarPromoMessage(null);
    try {
      // Try discount promo first (non-destructive)
      const response = await apiPost(API_URLS.PAYMENT_VALIDATE_PROMO, { code }, {}, false, '', true);
      setPromoDiscount(response.data.discount_percent);
      setPromoApplied(code);
    } catch (discountErr) {
      const isNotFound = discountErr.message?.includes('не найден');
      if (!isNotFound) {
        const match = discountErr.message.match(/\((.+)\)$/);
        setPromoError(match ? match[1] : t('Промокод не найден'));
        setPromoLoading(false);
        return;
      }
      // Not a discount promo — try star promo
      try {
        const res = await apiPost(API_URLS.PAYMENT_REDEEM_STAR_PROMO, { code }, {}, false, '', true);
        setUser(u => ({ ...u, balance: res.data.new_balance }));
        setStarPromoMessage({ type: 'ok', text: t('+{0} звёзд зачислено!', res.data.stars_added) });
        setPromoInput('');
      } catch (starErr) {
        const match = starErr.message?.match(/\((.+)\)$/);
        setPromoError(match ? match[1] : t('Промокод не найден'));
      }
    } finally {
      setPromoLoading(false);
    }
  };

  const handleWatchAd = async () => {
    if (adLoading) return;
    const ya = window.Ya;
    if (!ya?.Context?.AdvManager) {
      setAdMessage({ type: 'err', text: t('Реклама недоступна. Попробуйте позже.') });
      return;
    }
    setAdLoading(true);
    setAdMessage(null);

    let adToken = null;
    try {
      const res = await apiPost(API_URLS.AD_REWARD_TOKEN, {}, {}, false, '', true);
      adToken = res.data.token;
    } catch (err) {
      setAdLoading(false);
      const match = err.message?.match(/\((.+)\)$/);
      setAdMessage({ type: 'err', text: match ? match[1] : t('Не удалось запустить рекламу') });
      return;
    }

    const platform = ya.Context.AdvManager.getPlatform();
    const blockId = platform === 'desktop' ? 'R-A-19002032-1' : 'R-A-19002032-2';

    const timeout = setTimeout(() => {
      setAdLoading(false);
      setAdMessage({ type: 'err', text: t('Нет доступной рекламы. Попробуйте позже.') });
    }, 5000);

    ya.Context.AdvManager.render({
      blockId,
      type: 'rewarded',
      platform,
      onRewarded: async (isRewarded) => {
        clearTimeout(timeout);
        setAdLoading(false);
        if (!isRewarded) return;
        try {
          const res = await apiPost(API_URLS.AD_REWARD_CLAIM, { token: adToken }, {}, false, '', true);
          setUser(u => ({ ...u, balance: res.data.new_balance }));
          setAdMessage({ type: 'ok', text: t('+{0} звёзд! Спасибо за просмотр.', res.data.stars_added) });
        } catch (err) {
          const match = err.message?.match(/\((.+)\)$/);
          setAdMessage({ type: 'err', text: match ? match[1] : t('Не удалось начислить звёзды') });
        }
      },
    });
  };

  const activePlan = subscription?.plans?.find(p => p.id === subscription?.active_plan_id) || null;
  const activeCopy = activePlan ? PLAN_COPY[activePlan.id] : null;
  const showSubscription = subscription && (activePlan || subscription.plans?.length > 0);
  const subDiscount = subscription?.discount_percent || 0;
  // Only the best discount applies; the backend enforces the same rule when it charges.
  const effectiveDiscount = Math.max(subDiscount, promoDiscount || 0);

  // Russia rounds to whole rubles (as the backend charges); europe to cents, as Polar does.
  const discountedPrice = (original) => {
    if (!effectiveDiscount) return original;
    const discounted = original * (1 - effectiveDiscount / 100);
    return IS_EUROPE ? Math.round(discounted * 100) / 100 : Math.max(1, Math.round(discounted));
  };

  const handleSubscribe = async (plan) => {
    if (purchasingPlan !== null) return;
    if (!user?.username) return;
    const product = {
      id: `sub_${plan.id}`,
      name: `Подписка «${plan.name}»`,
      price: plan.rubles,
      category: 'Подписка',
      list: 'Подписки',
    };
    pushEcommerce({ click: { products: [product] } });
    setPurchasingPlan(plan.id);
    try {
      const response = await apiPost(API_URLS.SUBSCRIPTION_PURCHASE, { plan: plan.id });
      const { confirmation_url } = response.data;
      if (confirmation_url) {
        reportPayClick();
        // Leaving for the payment form is an add-to-cart and nothing more. The purchase is
        // pushed on the next page load, once the webhook has confirmed the money — see
        // utils/ecommerce.
        pushEcommerce({ add: { products: [{ ...product, quantity: 1 }] } });
        window.location.href = confirmation_url;
      }
    } catch (err) {
      console.error('Subscription payment failed:', err);
    } finally {
      setPurchasingPlan(null);
    }
  };

  const handlePurchase = async (pkg, index) => {
    if (purchasingIndex !== null) return;
    if (!user?.username) return;
    pushEcommerce({
      click: {
        products: [{
          id: String(pkg.stars),
          name: `${pkg.stars} звёзд`,
          price: discountedPrice(pkg.new_rubles),
          category: 'Звёзды',
          list: 'Пакеты',
          position: index + 1,
          ...(promoApplied && { coupon: promoApplied }),
        }],
      },
    });
    setPurchasingIndex(index);
    try {
      const payload = { amount_rubles: pkg.new_rubles };
      if (promoApplied) payload.promo_code = promoApplied;
      const response = await apiPost(API_URLS.PAYMENT_CREATE, payload);
      const { confirmation_url } = response.data;
      if (confirmation_url) {
        reportPayClick();
        const product = {
          id: String(pkg.stars),
          name: `${pkg.stars} звёзд`,
          price: discountedPrice(pkg.new_rubles),
          category: 'Звёзды',
          quantity: 1,
          ...(promoApplied && { coupon: promoApplied }),
        };
        // add-to-cart only: at this point the player has paid for nothing yet. The matching
        // purchase is pushed on their next page load, for the amount the webhook recorded.
        pushEcommerce({ add: { products: [product] } });
        window.location.href = confirmation_url;
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setPromoDiscount(null);
        setPromoApplied('');
        setPromoInput('');
        setPromoError(t('Промокод больше не действителен — скидка сброшена'));
      } else {
        console.error('Payment creation failed:', err);
      }
    } finally {
      setPurchasingIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="pp-wrap">
        <ParticleBackground />
        <div className="pp-loading">
          <div className="pp-spinner" />
          <span>{t('Загрузка…')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pp-wrap">
        <ParticleBackground />
        <div className="pp-error-page">
          <p>{t('Ошибка загрузки данных')}</p>
          <a className="pp-btn-back" href={localePath('/')}>
            <ChevronLeft size={16} /> {t('На главную')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-wrap">
      <ParticleBackground />

      <div className="pp-app">
        <div className="pp-header">
          <a href={localePath('/user/me')} className="pp-back">
            <ChevronLeft size={14} /> {t('Профиль')}
          </a>
          <h1 className="pp-title">{t('Пополнить счёт')}</h1>
        </div>

        {/* Balance */}
        <div className="pp-section">
          <h2 className="pp-section-title">
            <Star size={15} /> {t('Баланс')}
          </h2>
          <div className="pp-balance-display">
            <span className="pp-balance-num">{user?.balance ?? 0}</span>
            <span className="pp-balance-unit">{t('звёзд')}</span>
          </div>
          <p className="pp-turn-cost">{t('💬 Каждое сообщение персонажам стоит минимум 10 звёзд')}</p>
        </div>

        {/* Subscription */}
        {HAS_SUBSCRIPTIONS && showSubscription && (
          <div className="pp-section">
            <h2 className="pp-section-title">
              <Crown size={15} /> Тарифы
            </h2>

            <div className="pp-test-banner">
              <span className="pp-test-icon">🧪</span>
              <div>
                <div className="pp-test-title">Тестовый режим</div>
                <div className="pp-test-text">
                  Сейчас это <b>разовая покупка</b>. Автоматического списания через 30 дней не
                  будет — деньги не спишутся повторно. Продление — вручную, по желанию.
                </div>
              </div>
            </div>

            {activePlan ? (
              <article className={`pp-plan${activePlan.id === 'premium' ? ' pp-plan--featured' : ''}`}>
                <span className="pp-plan-ribbon">Ваш тариф</span>
                <h3 className="pp-plan-name">{activePlan.name}</h3>
                {activeCopy && <p className="pp-plan-tagline">{activeCopy.tagline}</p>}

                <div className="pp-plan-status">
                  Активен до <b>{new Date(subscription.end_date).toLocaleDateString('ru-RU')}</b>
                </div>

                {activeCopy && <PlanBullets perks={activeCopy.perks} />}

                {subscription.expiring_stars > 0 && (
                  <p className="pp-sub-warning">
                    ⚠️ <b>{ru(subscription.expiring_stars)} звёзд</b> сгорят{' '}
                    {new Date(subscription.end_date).toLocaleDateString('ru-RU')} — успейте потратить
                  </p>
                )}

                <p className="pp-sub-note">
                  Ежедневные {activePlan.daily_stars} звёзд забираются{' '}
                  <a href="/user/me">в профиле</a>
                  {subscription.daily_stars_available
                    ? ' — за сегодня ещё не получены.'
                    : ' — за сегодня уже получены.'}
                </p>
              </article>
            ) : (
              <div className="pp-plans">
                {subscription.plans.map((plan) => {
                  const copy = PLAN_COPY[plan.id];
                  if (!copy) return null;
                  const featured = plan.id === 'premium';
                  return (
                    <article key={plan.id} className={`pp-plan${featured ? ' pp-plan--featured' : ''}`}>
                      {copy.ribbon && <span className="pp-plan-ribbon">{copy.ribbon}</span>}
                      <h3 className="pp-plan-name">{plan.name}</h3>
                      <p className="pp-plan-tagline">{copy.tagline}</p>

                      <div className="pp-plan-price">{ru(plan.rubles)}<small>руб.</small></div>
                      <p className="pp-plan-per">{ru(plan.stars)} звёзд · до {ru(copy.turns)} ходов</p>
                      <span className={`pp-plan-save${featured ? ' pp-plan-save--gold' : ''}`}>{copy.savings}</span>

                      <PlanBullets perks={copy.perks} />

                      {user?.username ? (
                        <button
                          className={`pp-plan-cta${featured ? ' pp-plan-cta--gold' : ''}`}
                          disabled={purchasingPlan !== null}
                          onClick={() => handleSubscribe(plan)}
                        >
                          {purchasingPlan === plan.id ? 'Переход к оплате…' : `Купить ${plan.name}`}
                        </button>
                      ) : (
                        <span className={`pp-plan-cta${featured ? ' pp-plan-cta--gold' : ''} pp-plan-cta--disabled`}>
                          Войдите для покупки
                        </span>
                      )}
                      <p className="pp-plan-note">{PLAN_NOTE}</p>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Ad reward — the rewarded-ad SDK is a Russian ad network, so this whole block is
            russia-only rather than shown with a button that cannot load an ad. */}
        {HAS_AD_REWARD && (
        <div className={`pp-section pp-ad-section${!user?.username ? ' pp-ad-section--disabled' : ''}`}>
          <div className="pp-ad-header">
            <span className="pp-ad-icon">📺</span>
            <div>
              <h2 className="pp-promo-title">{t('Бесплатные звёзды')}</h2>
              <p className="pp-promo-subtitle">{user?.username ? t('Посмотрите рекламу и получите 1 звезду') : t('Только для зарегистрированных пользователей')}</p>
            </div>
          </div>
          {adMessage && (
            <p className={`pp-message pp-message--${adMessage.type}`}>{adMessage.text}</p>
          )}
          <button
            className="pp-ad-btn"
            onClick={handleWatchAd}
            disabled={adLoading || !user?.username}
          >
            {adLoading ? t('Загрузка…') : t('Смотреть рекламу')}
          </button>
        </div>
        )}

        {/* Promo code */}
        <div className={`pp-section pp-promo-section${promoDiscount ? ' pp-promo-section--active' : ''}`}>
          <div className="pp-promo-header">
            <span className="pp-promo-tag">%</span>
            <div>
              <h2 className="pp-promo-title">{t('Промокод')}</h2>
              <p className="pp-promo-subtitle">{user?.username ? t('Введи промокод — получи скидку или звёзды') : t('Только для зарегистрированных пользователей')}</p>
            </div>
          </div>
          <div className="pp-promo-row">
            <input
              className="pp-promo-input"
              type="text"
              placeholder={user?.username ? t('Введи промокод') : t('Только для зарегистрированных')}
              value={promoInput}
              onChange={e => { setPromoInput(e.target.value); setPromoError(null); setStarPromoMessage(null); }}
              onKeyDown={e => e.key === 'Enter' && applyPromo()}
              disabled={promoLoading || !!promoDiscount || !user?.username}
            />
            {promoDiscount ? (
              <button className="pp-promo-btn pp-promo-btn--remove" onClick={() => { setPromoDiscount(null); setPromoApplied(''); setPromoInput(''); }}>
                {t('Убрать')}
              </button>
            ) : (
              <button className="pp-promo-btn" onClick={applyPromo} disabled={promoLoading || !promoInput.trim() || !user?.username}>
                {promoLoading ? '…' : t('Применить')}
              </button>
            )}
          </div>
          {starPromoMessage && (
            <p className={`pp-message pp-message--${starPromoMessage.type}`}>{starPromoMessage.text}</p>
          )}
          {promoDiscount && (
            <p className="pp-promo-ok">
              {promoDiscount > subDiscount
                ? t('🎉 Скидка {0}% применена к ценам ниже!', promoDiscount)
                : t('Скидка по подписке −{0}% выгоднее — применяется она', subDiscount)}
            </p>
          )}
          {promoError && (
            <p className="pp-promo-err">{promoError}</p>
          )}
        </div>

        {/* Packages */}
        <div className="pp-section">
          <h2 className="pp-section-title">
            <CreditCard size={15} /> {t('Пакеты')}
          </h2>
          {paymentPackages.length > 0 && (
            <>
              {/* Polar is a merchant of record: it adds the buyer's national VAT on top of the
                  listed price at checkout, so the prices below are not what a European buyer's
                  card is charged. Saying so here, rather than letting the total change on Polar's
                  page without warning. Russia's prices include everything already. */}
              {IS_EUROPE && (
                <p className="pp-tax-note">
                  {t('Цены указаны без налога. Итоговая сумма с НДС вашей страны будет показана при оплате.')}
                </p>
              )}
              <p className="pp-packages-label">
                {t('Быстрая покупка')}{effectiveDiscount ? t(' · скидка {0}%', effectiveDiscount) : ''}
              </p>
              <div className="pp-packages">
                {[...paymentPackages].sort((a, b) => a.new_rubles - b.new_rubles).map((pkg, index, arr) => (
                  <div key={index} className={`pp-package-card${index === arr.length - 1 ? ' pp-package-card--hot' : ''}`}>
                    <div className="pp-package-pricing">
                      {(pkg.old_rubles !== pkg.new_rubles || effectiveDiscount > 0) && (
                        <span className="pp-package-old"><span className="pp-package-old-num">{money(pkg.new_rubles)}</span></span>
                      )}
                      <span className="pp-package-new">{money(discountedPrice(pkg.new_rubles))}</span>
                    </div>
                    <div className={`pp-package-stars${IS_EUROPE ? '' : ' pp-package-stars--last'}`}>{t('{0} звёзд', pkg.stars)}</div>
                    {/* A turn costs at least 10 stars, so the turn count is a ceiling and the
                        per-turn price a floor: the cheapest a turn can be on this pack. */}
                    {IS_EUROPE && (
                      <>
                        <div className="pp-package-turns">{t('до {0} сообщений персонажам', pkg.turns)}</div>
                        <div className="pp-package-per-turn">{t('от {0} за сообщение', money(discountedPrice(pkg.new_rubles) / pkg.turns, 3))}</div>
                      </>
                    )}
                    {user?.username ? (
                      <button
                        className="pp-package-btn"
                        disabled={purchasingIndex !== null}
                        onClick={() => handlePurchase(pkg, index)}
                      >
                        {purchasingIndex === index ? t('Переход к оплате…') : t('Купить')}
                      </button>
                    ) : (
                      <span className="pp-package-btn" style={{ opacity: 0.5, cursor: 'default' }}>{t('Войдите для покупки')}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* What Stars actually are, and who takes the money. Required disclosure: a virtual
              currency has to be identified as one — it is not a refundable balance — and the
              payment processor has to be named on the page where the charge is made. */}
          <p className="pp-payment-consent">
            {t('Звёзды — виртуальные единицы, которые используются только внутри платформы для отправки сообщений персонажам. Они не являются денежными средствами и не подлежат обмену обратно на деньги.')}
          </p>
          <p className="pp-payment-consent">
            {t('Пополняя счёт, вы соглашаетесь с условиями оказания услуг, размещёнными в')}{' '}
            <a href={localePath('/terms')} target="_blank" rel="noopener noreferrer">{t('Пользовательском соглашении')}</a>.
            {' '}{t('Платежи обрабатываются через {0}.', PAYMENT_PROCESSOR)}
          </p>
        </div>

        {/* Payment history */}
        {paymentHistory.length > 0 && (
          <div className="pp-section">
            <h2 className="pp-section-title">
              <Clock size={15} /> {t('История платежей')}
            </h2>
            <div className="pp-payment-list">
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="pp-payment-item">
                  <div>
                    <span className="pp-payment-amount">{money(payment.amount_rubles)}</span>
                    <span className="pp-payment-stars">
                      {payment.subscription_name
                        ? t('Подписка «{0}»', payment.subscription_name)
                        : t('+{0} звёзд', payment.points_added)}
                    </span>
                  </div>
                  <span className="pp-payment-date">
                    {new Date(payment.payment_date).toLocaleDateString('ru-RU')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="pp-spacer" />
      </div>
    </div>
  );
};

export default PaymentPage;
